import React, { useState, useEffect } from 'react';
import './FeedbackPopup.css';

const FeedbackPopup: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed the popup today
    const lastDismissed = localStorage.getItem('feedbackPopupDismissed');
    const today = new Date().toDateString();
    
    if (lastDismissed === today) {
      return; // Don't show popup if dismissed today
    }
    
    // Check if demo tour has been completed
    const hasCompletedTour = getCookie('demoTourCompleted');
    
    if (hasCompletedTour) {
      // Show popup after 3 minutes if tour is completed
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 180000);
      return () => clearTimeout(timer);
    } else {
      // Listen for demo tour completion event
      const handleTourComplete = () => {
        // Show popup after 3 minutes when tour completes
        setTimeout(() => {
          setIsVisible(true);
        }, 180000);
      };

      window.addEventListener('demoTourCompleted', handleTourComplete);
      
      return () => {
        window.removeEventListener('demoTourCompleted', handleTourComplete);
      };
    }
  }, []);

  // Helper function to get cookie value
  const getCookie = (name: string): string | null => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
    return null;
  };

  const handleClose = () => {
    // Remember that user dismissed the popup today
    localStorage.setItem('feedbackPopupDismissed', new Date().toDateString());
    
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleFeedbackClick = () => {
    window.open(
      'https://forms.office.com/Pages/ResponsePage.aspx?id=EByzxFUNhUip6u1tDIKBJHTLTd8F4zhDtql0HAq0FzJUMEFGRTdBSjJKNldWM1lSTVpKMlFIS1o2QiQlQCN0PWcu',
      '_blank',
      'noopener,noreferrer'
    );
    handleClose();
  };

  if (!isVisible) return null;

  return (
    <div className={`feedback-popup-overlay ${isClosing ? 'closing' : ''}`}>
      <div className={`feedback-popup ${isClosing ? 'closing' : ''}`}>
        <button 
          className="feedback-close-button" 
          onClick={handleClose}
          title="Close Modal"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
          Close
        </button>
        
        <div className="feedback-content">
          <div className="feedback-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          
          <h3 className="feedback-title">Your Feedback Matters!</h3>
          
          <p className="feedback-message">
            Help us improve your experience. Your feedback is invaluable in making this platform better for everyone.
          </p>
          
          <div className="feedback-actions">
            <button 
              className="feedback-primary-button"
              onClick={handleFeedbackClick}
            >
              <svg className="feedback-hat-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Give Feedback
            </button>
            
            <button 
              className="feedback-secondary-button"
              onClick={handleClose}
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeedbackPopup;
